/**
 * Advanced FAQ functionality
 * Handles search, filtering, and category navigation
 */

class AdvancedFAQ {
  constructor(section) {
    this.section = section;
    this.categoryButtons = section.querySelectorAll('.faq-category-btn');
    this.categoriesEl = section.querySelector('.faq-categories');
    this.faqItems = section.querySelectorAll('.faq-item');
    this.noResults = section.querySelector('.faq-no-results');
    this.resultsStatus = section.querySelector('.faq-results-status');
    this.searchInput = section.querySelector('.faq-search__input');
    this.searchClear = section.querySelector('.faq-search__clear');
    this.searchQuery = '';
    this.hasInteracted = false;
    
    // Set initial category to the first active button's category
    const activeButton = section.querySelector('.faq-category-btn.active');
    this.currentCategory = activeButton ? activeButton.dataset.category : 'all';
    
    this.init();
  }

  init() {
    // Category filtering
    this.categoryButtons.forEach(button => {
      button.addEventListener('click', this.handleCategoryClick.bind(this));
    });

    // Live search
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.handleSearch.bind(this));
    }
    if (this.searchClear) {
      this.searchClear.addEventListener('click', () => {
        if (this.searchInput) this.searchInput.value = '';
        this.handleSearch();
        if (this.searchInput) this.searchInput.focus();
      });
    }

    // Initial filter on page load
    this.filterItems();

    // Auto-expand if URL has hash
    this.handleUrlHash();

    // Close other accordions when opening one (optional)
    this.faqItems.forEach(item => {
      const details = item.querySelector('details');
      if (details) {
        details.addEventListener('toggle', () => {
          if (details.open) {
            this.updateUrlHash(item.id);
          }
        });
      }
    });
  }

  handleSearch() {
    this.hasInteracted = true;
    this.searchQuery = (this.searchInput ? this.searchInput.value : '').trim().toLowerCase();
    const searching = this.searchQuery.length > 0;

    if (this.searchClear) this.searchClear.hidden = !searching;
    if (this.categoriesEl) this.categoriesEl.classList.toggle('faq-categories--searching', searching);

    this.filterItems();
  }

  handleCategoryClick(e) {
    this.hasInteracted = true;
    const button = e.currentTarget;
    const category = button.dataset.category;

    // Picking a category clears any active search
    if (this.searchQuery) {
      if (this.searchInput) this.searchInput.value = '';
      this.searchQuery = '';
      if (this.searchClear) this.searchClear.hidden = true;
      if (this.categoriesEl) this.categoriesEl.classList.remove('faq-categories--searching');
    }
    
    // Update active state
    this.categoryButtons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-pressed', 'false');
    });
    button.classList.add('active');
    button.setAttribute('aria-pressed', 'true');
    
    this.currentCategory = category;
    this.filterItems();
  }

  filterItems() {
    let visibleCount = 0;
    const searching = this.searchQuery.length > 0;
    
    this.faqItems.forEach(item => {
      // While searching, look across every category. Otherwise honour the active category.
      const categoryMatch = searching ||
                           this.currentCategory === 'all' ||
                           item.dataset.category === this.currentCategory;
      const searchMatch = !searching ||
                          (item.dataset.searchText || '').indexOf(this.searchQuery) !== -1;
      
      if (categoryMatch && searchMatch) {
        item.classList.remove('hidden');
        visibleCount++;
      } else {
        item.classList.add('hidden');
      }
    });
    
    // Show/hide no results message
    if (this.noResults) {
      this.noResults.style.display = visibleCount === 0 ? 'block' : 'none';
    }

    // Announce the filtered result count to screen readers (only after a
    // user-initiated filter - never unprompted on page load)
    if (this.resultsStatus && this.hasInteracted) {
      this.resultsStatus.textContent =
        visibleCount === 1 ? '1 question shown' : visibleCount + ' questions shown';
    }

    // Update category counts
    this.updateCategoryCounts();
  }

  updateCategoryCounts() {
    this.categoryButtons.forEach(button => {
      const category = button.dataset.category;
      const countElement = button.querySelector('.faq-category-count');
      
      if (countElement) {
        let count = 0;
        
        this.faqItems.forEach(item => {
          if (category === 'all' || item.dataset.category === category) {
            count++;
          }
        });
        
        countElement.textContent = count;
      }
    });
  }

  handleUrlHash() {
    const hash = window.location.hash.substring(1);
    if (hash) {
      const targetItem = this.section.querySelector(`#${hash}`);
      if (targetItem) {
        const details = targetItem.querySelector('details');
        if (details) {
          details.open = true;
          setTimeout(() => {
            targetItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
      }
    }
  }

  updateUrlHash(itemId) {
    if (history.replaceState) {
      history.replaceState(null, null, `#${itemId}`);
    }
  }
}

// Initialize all FAQ sections
document.addEventListener('DOMContentLoaded', () => {
  const faqSections = document.querySelectorAll('.faq-advanced');
  faqSections.forEach(section => {
    new AdvancedFAQ(section);
  });
});

// Handle browser back/forward with hash changes
window.addEventListener('hashchange', () => {
  const faqSections = document.querySelectorAll('.faq-advanced');
  faqSections.forEach(section => {
    const faq = new AdvancedFAQ(section);
    faq.handleUrlHash();
  });
});
